import { useState, useEffect, useCallback } from "react";
import { authHeaders } from "../supabase";

const API = process.env.REACT_APP_API_URL || "https://api.mioprice.com";
const DEMO_USER = "jularyy@gmail.com";

const PAYER_LABELS = { uhc: "UHC", aetna: "Aetna", cigna: "Cigna", kaiser: "Kaiser", bcbs: "BCBS" };
const PAYER_COLORS = {
  uhc:    { bg: "#dbeafe", text: "#1d4ed8" },
  aetna:  { bg: "#fae8ff", text: "#7e22ce" },
  cigna:  { bg: "#dcfce7", text: "#15803d" },
  kaiser: { bg: "#fff7ed", text: "#c2410c" },
  bcbs:   { bg: "#fef3c7", text: "#b45309" },
};

const fmt  = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();
const isZip = (s) => /^\d{5}$/.test((s || "").trim());

// City geocode
const geoCache = {};
async function geocodeCity(input) {
  const key = input.toLowerCase().trim();
  if (geoCache[key]) return geoCache[key];
  try {
    const q = encodeURIComponent(key + (key.includes(",") ? "" : ", USA"));
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=3&q=${q}&countrycodes=us&addressdetails=1`,
      { headers: { "User-Agent": "MioPrice/1.0" } }
    );
    if (r.ok) {
      const d = await r.json();
      if (d.length > 0) {
        const best = d.find(x => ["city","town"].includes(x.type)) || d[0];
        const city  = best.address?.city || best.address?.town || (best.display_name||"").split(",")[0];
        const state = best.address?.state_code || "";
        const result = { lat: parseFloat(best.lat), lng: parseFloat(best.lon),
                         label: (city||"").trim() + (state ? ", " + state : "") };
        geoCache[key] = result;
        return result;
      }
    }
  } catch {}
  return null;
}

// ── Summary row per payer ─────────────────────────────────────────────────────
const FEE_LABEL = { professional: "Physician fee", institutional: "Facility fee" };

function PayerSummaryRow({ payer, opp, showDetail, onToggle }) {
  const pc  = PAYER_COLORS[payer] || { bg: "#f3f4f6", text: "#374151" };
  const lbl = PAYER_LABELS[payer] || payer.toUpperCase();
  const byBC   = opp.by_billing_class || [];
  const canNeg = opp.opportunity != null && opp.opportunity > 0;

  return (
    <>
      {byBC.length === 0 ? (
        <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
          <td style={{ padding: "12px 14px" }}><span style={{ fontWeight: 700, color: pc.text, background: pc.bg, padding: "3px 10px", borderRadius: 999, fontSize: 13 }}>{lbl}</span></td>
          <td colSpan={5} style={{ padding: "12px 14px", color: "#d1d5db", fontSize: 12, textAlign: "center" }}>Not contracted</td>
          <td></td>
        </tr>
      ) : byBC.map((bc, idx) => (
        <tr key={bc.billing_class} style={{ borderBottom: "1px solid #f3f4f6", background: showDetail && bc.detail ? "#f0fdf4" : idx % 2 === 0 ? "#fff" : "#fafafa" }}>
          <td style={{ padding: "10px 14px" }}>
            {idx === 0 && <span style={{ fontWeight: 700, color: pc.text, background: pc.bg, padding: "3px 10px", borderRadius: 999, fontSize: 13, display: "block", marginBottom: 4 }}>{lbl}</span>}
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{FEE_LABEL[bc.billing_class] || bc.billing_class}</span>
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, fontSize: 14 }}>
            {bc.your_min
              ? <div><div>{fmt(bc.your_min)}</div>{bc.your_max && bc.your_max !== bc.your_min && <div style={{fontSize:10,color:"#9ca3af"}}>–{fmt(bc.your_max)}</div>}</div>
              : <span style={{ color: "#d1d5db", fontSize: 12 }}>—</span>}
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center", color: "#374151" }}>
            {fmt(bc.peers_above_avg)}
            {bc.peers_above_count > 0 && <div style={{ fontSize: 10, color: "#9ca3af" }}>{bc.peers_above_count} providers</div>}
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center", color: "#374151" }}>
            {fmt(bc.peers_below_avg)}
            {bc.peers_below_count > 0 && <div style={{ fontSize: 10, color: "#9ca3af" }}>{bc.peers_below_count} providers</div>}
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center" }}>
            {bc.peer_max > 20000
              ? <span title="May include MRF outlier">{fmt(bc.peer_max)} <span style={{fontSize:9,color:"#f59e0b"}}>⚠</span></span>
              : fmt(bc.peer_max)}
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center" }}>
            {bc.opportunity == null ? <span style={{ color: "#d1d5db" }}>—</span>
             : bc.opportunity <= 0 ? <span style={{ fontSize: 12, color: "#6b7280" }}>At/above avg</span>
             : <div><div style={{ fontWeight: 800, color: "#15803d", fontSize: 14 }}>+{fmt(bc.opportunity)}</div><div style={{ fontSize: 10, color: "#9ca3af" }}>negotiate up</div></div>}
          </td>
          <td style={{ padding: "10px 14px", textAlign: "center" }}>
            {idx === 0 && (bc.detail?.above?.length > 0 || bc.detail?.below?.length > 0) && (
              <button onClick={onToggle} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 8, border: "1px solid #e5e7eb", background: showDetail ? "#dcfce7" : "#fff", color: showDetail ? "#15803d" : "#374151", cursor: "pointer" }}>
                {showDetail ? "▲ Hide" : "▼ Details"}
              </button>
            )}
          </td>
        </tr>
      ))}
      {showDetail && byBC.some(bc => bc.detail?.above?.length > 0 || bc.detail?.below?.length > 0) && (
        <tr>
          <td colSpan={7} style={{ padding: "0 14px 14px 14px", background: "#f0fdf4" }}>
            {byBC.map(bc => (
              (bc.detail?.above?.length > 0 || bc.detail?.below?.length > 0) && (
                <div key={bc.billing_class} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8, textTransform: "uppercase", borderBottom: "1px solid #d1fae5", paddingBottom: 4 }}>
                    {FEE_LABEL[bc.billing_class] || bc.billing_class}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {bc.detail?.above?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", marginBottom: 6 }}>Paid more than you ({bc.detail.above.length})</div>
                        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                          <thead><tr style={{ borderBottom: "1px solid #bbf7d0" }}>
                            <th style={{ padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: 600 }}>Provider</th>
                            <th style={{ padding: "4px 8px", textAlign: "center", color: "#6b7280", fontWeight: 600 }}>Rate</th>
                            <th style={{ padding: "4px 8px", textAlign: "center", color: "#6b7280", fontWeight: 600 }}>vs You</th>
                          </tr></thead>
                          <tbody>{bc.detail.above.map((p, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #dcfce7" }}>
                              <td style={{ padding: "5px 8px", fontWeight: 500 }}>{p.name || p.npi}</td>
                              <td style={{ padding: "5px 8px", textAlign: "center", fontWeight: 700, color: "#15803d" }}>{fmt(p.min)}</td>
                              <td style={{ padding: "5px 8px", textAlign: "center", color: "#15803d", fontSize: 11 }}>{bc.your_min ? "+" + fmt(p.min - bc.your_min) : "—"}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    {bc.detail?.below?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>Paid less than you ({bc.detail.below.length})</div>
                        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                          <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                            <th style={{ padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: 600 }}>Provider</th>
                            <th style={{ padding: "4px 8px", textAlign: "center", color: "#6b7280", fontWeight: 600 }}>Rate</th>
                            <th style={{ padding: "4px 8px", textAlign: "center", color: "#6b7280", fontWeight: 600 }}>vs You</th>
                          </tr></thead>
                          <tbody>{bc.detail.below.map((p, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                              <td style={{ padding: "5px 8px", color: "#374151" }}>{p.name || p.npi}</td>
                              <td style={{ padding: "5px 8px", textAlign: "center", color: "#6b7280" }}>{fmt(p.min)}</td>
                              <td style={{ padding: "5px 8px", textAlign: "center", color: "#ef4444", fontSize: 11 }}>{bc.your_min ? fmt(p.min - bc.your_min) : "—"}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ProviderIntelligence() {
  const [npiInput,     setNpiInput]     = useState("");
  const [provName,     setProvName]     = useState("");
  const [cpt,          setCpt]          = useState("");
  const [cptLabel,     setCptLabel]     = useState("");
  const [location,     setLocation]     = useState("");
  const [locationLabel,setLocationLabel]= useState("");
  const [radius,       setRadius]       = useState(15);
  const [peerCount,    setPeerCount]    = useState(10);
  const [running,      setRunning]      = useState(false);
  const [data,         setData]         = useState(null);
  const [error,        setError]        = useState(null);
  const [expandedPayer,setExpandedPayer]= useState(null);

  const [searchTerms,  setSearchTerms]  = useState([]);
  const [termMap,      setTermMap]      = useState({});
  const [cityList,     setCityList]     = useState([]);
  const [procSugs,     setProcSugs]     = useState([]);
  const [showProcSug,  setShowProcSug]  = useState(false);
  const [citySugs,     setCitySugs]     = useState([]);
  const [showCitySug,  setShowCitySug]  = useState(false);
  const [npiSugs,      setNpiSugs]      = useState([]);
  const [showNpiSug,   setShowNpiSug]   = useState(false);

  useEffect(() => {
    fetch(API + "/api/search-terms").then(r=>r.json()).then(d => {
      if (d.terms) {
        const tc = s => s.replace(/\b\w/g, c => c.toUpperCase());
        setSearchTerms([...new Set(d.terms.map(t => tc(t.term)))].sort());
        const map = {}; d.terms.forEach(t => { map[tc(t.term)] = t.cpt; map[t.term.toLowerCase()] = t.cpt; });
        setTermMap(map);
      }
    }).catch(()=>{});
    fetch(API + "/api/cities").then(r=>r.json()).then(d => { if(d.cities) setCityList(d.cities); }).catch(()=>{});
  }, []);

  // NPI search via provider name
  const searchNpi = useCallback(async (name) => {
    if (name.length < 3) { setNpiSugs([]); setShowNpiSug(false); return; }
    try {
      // Use our NPI reference via healthcare API with just name search
      const r = await fetch(`${API}/api/healthcare?query=office+visit&loc=${encodeURIComponent(name)}&limit=5`);
      // Fallback: just show NPI input hint
    } catch {}
  }, []);

  const runSearch = useCallback(async () => {
    const npi = npiInput.trim();
    const loc = location.trim();
    if (!npi || !cpt) return;
    setRunning(true); setData(null); setError(null); setExpandedPayer(null);

    try {
      let lat, lng, state = "IL";
      if (loc) {
        if (isZip(loc)) {
          // derive state from zip
          state = loc.startsWith("9") ? "CA" : "IL";
        } else {
          const geo = await geocodeCity(loc);
          if (geo) { lat = geo.lat; lng = geo.lng; setLocationLabel(geo.label); state = geo.label.includes(", CA") ? "CA" : "IL"; }
        }
      }

      const p = new URLSearchParams({ npi, cpt, radius, state, peer_count: peerCount });
      if (lat) { p.set("lat", lat); p.set("lng", lng); }

      const r = await fetch(API + "/api/provider-intelligence-v2?" + p, { headers: await authHeaders() });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Request failed"); setRunning(false); return; }
      setData(d);
      setProvName(d.provider?.name || npi);
    } catch(e) { setError(e.message); }
    setRunning(false);
  }, [npiInput, cpt, location, radius, peerCount]);

  const inputStyle = { width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 9, padding: "10px 12px", fontSize: 14, boxSizing: "border-box", fontFamily: "inherit" };
  const labelStyle = { fontSize: 11, color: "#6b7280", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 16px", fontFamily: "inherit" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: "#111827" }}>Provider Intelligence</span>
          <span style={{ fontSize: 11, fontWeight: 600, background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 999 }}>BETA</span>
        </div>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          See what payers are paying your peers — and identify where you're being underpaid. Same federal MRF data, provider perspective.
        </p>
      </div>

      {/* Search */}
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>

          {/* NPI */}
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={labelStyle}>Your NPI</div>
            <input value={npiInput} onChange={e => setNpiInput(e.target.value)}
              placeholder="e.g. 1487648950"
              style={inputStyle} />
          </div>

          {/* Procedure */}
          <div style={{ flex: 2, minWidth: 180, position: "relative" }}>
            <div style={labelStyle}>Procedure</div>
            <input value={cptLabel || cpt}
              onChange={e => {
                const v = e.target.value; setCptLabel(v);
                const code = termMap[v] || termMap[v.toLowerCase()] || v; setCpt(code);
                if (v.length >= 2) { const m = searchTerms.filter(s => s.toLowerCase().includes(v.toLowerCase())).slice(0,8); setProcSugs(m); setShowProcSug(m.length>0); }
                else { setProcSugs([]); setShowProcSug(false); }
              }}
              onBlur={() => setTimeout(() => setShowProcSug(false), 150)}
              placeholder="Search procedure…" style={inputStyle} />
            {showProcSug && procSugs.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden", maxHeight: 220, overflowY: "auto" }}>
                {procSugs.map((s,i) => (
                  <div key={s} onMouseDown={() => { setCptLabel(s); const c = termMap[s]||termMap[s.toLowerCase()]||s; setCpt(c); setProcSugs([]); setShowProcSug(false); }}
                    style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: i<procSugs.length-1?"1px solid #f3f4f6":"none" }}>
                    🔍 {s}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Market */}
          <div style={{ flex: 1, minWidth: 140, position: "relative" }}>
            <div style={labelStyle}>Market (optional)</div>
            <input value={locationLabel || location}
              onChange={e => {
                const v = e.target.value; setLocation(v); setLocationLabel("");
                if (v.length >= 2 && !isZip(v)) { const m = cityList.filter(c => c.toLowerCase().startsWith(v.toLowerCase())).slice(0,6); setCitySugs(m); setShowCitySug(m.length>0); }
                else { setCitySugs([]); setShowCitySug(false); }
              }}
              onBlur={() => setTimeout(() => setShowCitySug(false), 150)}
              placeholder="City or ZIP" style={inputStyle} />
            {showCitySug && citySugs.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 9, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, overflow: "hidden" }}>
                {citySugs.map((city,i) => (
                  <div key={city} onMouseDown={() => { setLocation(city); setLocationLabel(city); setCitySugs([]); setShowCitySug(false); }}
                    style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: i<citySugs.length-1?"1px solid #f3f4f6":"none" }}>
                    📍 {city}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Radius */}
          <div style={{ minWidth: 90 }}>
            <div style={labelStyle}>Radius</div>
            <select value={radius} onChange={e => setRadius(Number(e.target.value))}
              style={{ ...inputStyle, background: "#fff" }}>
              {[5,10,15,20,30,50].map(r => <option key={r} value={r}>{r} mi</option>)}
            </select>
          </div>

          {/* Peer count */}
          <div style={{ minWidth: 90 }}>
            <div style={labelStyle}>Peers shown</div>
            <select value={peerCount} onChange={e => setPeerCount(Number(e.target.value))}
              style={{ ...inputStyle, background: "#fff" }}>
              {[5,10,15,20,25].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <button onClick={runSearch} disabled={running || !npiInput.trim() || !cpt.trim()}
            style={{ padding: "10px 20px", background: running || !npiInput.trim() || !cpt.trim() ? "#93c5fd" : "#2563eb", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            {running ? "Analyzing…" : "Compare Peers"}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", color: "#b91c1c", fontSize: 13, marginBottom: 16 }}>
          ⚠️ {error}
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              {provName || data.provider?.name}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {data.provider?.city} · {cptLabel || cpt} · {data.peer_count} peer providers in {radius}mi radius · showing top {peerCount} above/below
            </div>
          </div>

          {/* ── Summary banner ── */}
          {(() => {
            const totalOpp = data.opportunities.reduce((s,o) => s+(o.opportunity>0?o.opportunity:0), 0);
            const uncontracted = data.opportunities.filter(o => o.by_billing_class.every(bc => !bc.your_min));
            const uncontractedPeerMax = uncontracted.length > 0
              ? Math.max(...uncontracted.flatMap(o => o.by_billing_class.map(bc => bc.peers_above_avg||0)))
              : 0;
            return (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                {totalOpp > 0 && (
                  <div style={{ flex: 1, minWidth: 200, background: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "1.5px solid #86efac", borderRadius: 12, padding: "14px 18px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>Total Negotiation Opportunity</div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: "#15803d" }}>+{fmt(totalOpp)}</div>
                    <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>per procedure vs peer average</div>
                  </div>
                )}
                {uncontracted.length > 0 && (
                  <div style={{ flex: 1, minWidth: 200, background: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "1.5px solid #fcd34d", borderRadius: 12, padding: "14px 18px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>💡 Uncontracted Payers</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: "#b45309" }}>{uncontracted.map(o => PAYER_LABELS[o.payer]||o.payer.toUpperCase()).join(", ")}</div>
                    <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>Peers avg {fmt(uncontractedPeerMax)}/procedure — potential new revenue</div>
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e5e7eb" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#374151", minWidth: 100 }}>Payer</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#1d4ed8", minWidth: 110 }}>Your Rate</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#374151", minWidth: 120 }}>Peers Above Avg</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#374151", minWidth: 120 }}>Peers Below Avg</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#374151", minWidth: 100 }}>Market Max</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#15803d", minWidth: 120 }}>Opportunity</th>
                  <th style={{ padding: "10px 14px", textAlign: "center", minWidth: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {data.opportunities.map(opp => (
                  <PayerSummaryRow
                    key={opp.payer}
                    payer={opp.payer}
                    opp={opp}
                    showDetail={expandedPayer === opp.payer}
                    onToggle={() => setExpandedPayer(expandedPayer === opp.payer ? null : opp.payer)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, marginBottom: 4 }}>
            <button onClick={() => {
              const totalOpp = data.opportunities.reduce((s,o)=>s+(o.opportunity>0?o.opportunity:0),0);
              const lines = data.opportunities.map(o => {
                const best = o.by_billing_class.find(bc => bc.opportunity > 0);
                return best ? `${PAYER_LABELS[o.payer]||o.payer}: +${fmt(best.opportunity)}/procedure (${FEE_LABEL[best.billing_class]||best.billing_class})` : null;
              }).filter(Boolean);
              const uncon = data.opportunities.filter(o=>o.by_billing_class.every(bc=>!bc.your_min)).map(o=>PAYER_LABELS[o.payer]||o.payer).join(", ");
              const txt = `Provider Intelligence Summary\n${data.provider?.name}\n${data.provider?.city} · ${cptLabel||cpt}\n\nTotal opportunity: +${fmt(totalOpp)}/procedure\n\n${lines.join("\n")}${uncon ? "\n\nUncontracted payers: "+uncon : ""}\n\nSource: MioPrice — Transparency in Coverage data (45 CFR § 180)`;
              navigator.clipboard.writeText(txt).then(()=>alert("Summary copied!")).catch(()=>alert("Copy failed — try manually"));
            }} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer", color: "#374151" }}>
              📋 Copy summary
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4, lineHeight: 1.6 }}>
            Rates from federal Transparency in Coverage MRFs. Peer providers are within {radius}mi of your location. Data is public under 45 CFR § 180.
          </p>
          <div style={{ marginTop: 8, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
            <span style={{ fontWeight: 700 }}>⚠ Data Quality Note:</span> Market Max values flagged with ⚠ exceed $20,000 and may reflect MRF filing errors by payers — such as per-diem rates, bundled episode rates, or incorrect rate types. These should be used as directional reference only, not as a negotiation ceiling. MioPrice Enterprise includes automatic outlier filtering.
          </div>
        </>
      )}

      {!data && !running && !error && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Know your market position</div>
          <div style={{ fontSize: 13, maxWidth: 420, margin: "0 auto" }}>
            Enter your NPI and a procedure to see what payers are paying your peers — and where you have room to negotiate up.
          </div>
        </div>
      )}
    </div>
  );
}
